import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createDiagnostics,
  createDiagnosticsByPetId,
  createPrescriptionWoLoader,
  createProcedure,
  updateDiagnostics,
  updatePrescriptionByIdWoLoader,
  updateProcedureById,
  updateProcedureDateAndRemarkById,
} from "../../../redux/reducers/petSlice";
import { diagnosticTypes } from "../../../util/dropList";
import { getProcedureDetailsNew } from "../../../util/function";

const initValues = {
  type: "Preventive",
  diagType: "Lab",
  service: null,
  variant: null,
  date: new Date(),
  remarks: "",
  addToAppointment: false,
  planItem: "",
  doseInSeries: null,
};
const initErrs = { service: "", variant: "", planItem: "", doseInSeries: "" };
const defObj = {
  clinic: {},
  services: [],
  stocks: [],
  facilities: [],
  locations: [],
  serviceRemainders: [],
};

const getRemainderId = (tmp) =>
  tmp?.serviceRemainderId ?? tmp?.templateId ?? tmp?.id ?? null;

const getRemainderSteps = (tmp) => {
  if (Array.isArray(tmp?.steps) && tmp.steps.length) return tmp.steps;
  if (
    Array.isArray(tmp?.serviceRemainderSteps) &&
    tmp.serviceRemainderSteps.length
  ) {
    return tmp.serviceRemainderSteps;
  }
  return [];
};

const parseDueAfterDays = (st) => {
  const n = Number(st?.dueAfterDays ?? st?.due_after_days ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const getDoseInSeriesList = (remainders, serviceId) => {
  if (serviceId == null || serviceId === "") return [];
  const linkedTemps = (Array.isArray(remainders) ? remainders : []).filter(
    (tmp) => {
      const linked = (tmp?.linkedServices ?? tmp?.serviceIds ?? [])
        ?.map((ser) =>
          typeof ser === "object" && ser !== null
            ? (ser?.serviceId ?? ser?.value)
            : ser,
        )
        ?.filter((id) => id != null && id !== "");
      return linked?.some((id) => String(id) === String(serviceId));
    },
  );
  const prefixTemplate = linkedTemps.length > 1;
  const options = [];
  linkedTemps.forEach((tmp) => {
    const steps = getRemainderSteps(tmp);
    if (!steps.length) return;
    const occurrence = tmp?.occurrenceLabel?.trim() || "Dose";
    const remainderId = getRemainderId(tmp);
    const templateName = tmp?.templateName ?? tmp?.name ?? "";
    steps.forEach((st, index) => {
      const stepName = st?.stepName ?? st?.name ?? `${occurrence} ${index + 1}`;
      const stepId = st?.stepId ?? st?.id ?? `${remainderId}-${index + 1}`;
      const displayName =
        prefixTemplate && templateName
          ? `${templateName} · ${stepName}`
          : stepName;
      options.push({
        name: displayName,
        value: displayName,
        stepId,
        stepName,
        stepOrder: Number(st?.stepOrder ?? st?.order ?? index + 1),
        dueAfterDays: parseDueAfterDays(st),
        serviceRemainderId: remainderId,
      });
    });
  });
  return options;
};

const getOccurrenceLabel = (remainders, serviceId) => {
  if (serviceId == null || serviceId === "") return "Dose";
  const tmp = (Array.isArray(remainders) ? remainders : []).find((t) => {
    const linked = (t?.linkedServices ?? t?.serviceIds ?? [])
      ?.map((ser) =>
        typeof ser === "object" && ser !== null
          ? (ser?.serviceId ?? ser?.value)
          : ser,
      )
      ?.filter((id) => id != null && id !== "");
    return linked?.some((id) => String(id) === String(serviceId));
  });
  return tmp?.occurrenceLabel?.trim() || "Dose";
};

const findDoseOption = (options, item) => {
  const doseVal = item?.doseInSeries?.stepId ?? item?.doseInSeries;
  if (doseVal == null || doseVal === "") return null;
  const remainderId =
    item?.doseInSeries?.serviceRemainderId ?? item?.serviceRemainderId;
  return (
    (options ?? []).find((d) => {
      const sameRemainder =
        remainderId == null ||
        remainderId === "" ||
        String(d?.serviceRemainderId) === String(remainderId);
      return (
        sameRemainder &&
        (String(d?.stepId) === String(doseVal) ||
          String(d?.stepOrder) === String(doseVal))
      );
    }) ?? null
  );
};

const getNextRemainderStep = (
  remainders,
  serviceId,
  currentDose,
  selectedItem,
) => {
  try {
    const options = getDoseInSeriesList(remainders, serviceId);
    if (!options.length) return null;
    const currentStepId =
      currentDose?.stepId ??
      selectedItem?.doseInSeries?.stepId ??
      selectedItem?.doseInSeries;
    if (currentStepId == null || currentStepId === "") return null;
    const remainderId =
      currentDose?.serviceRemainderId ?? selectedItem?.serviceRemainderId;
    const currentIndex = options.findIndex((d) => {
      const sameRemainder =
        remainderId == null ||
        remainderId === "" ||
        String(d?.serviceRemainderId) === String(remainderId);
      return (
        sameRemainder &&
        (String(d?.stepId) === String(currentStepId) ||
          String(d?.stepOrder) === String(currentStepId))
      );
    });
    if (currentIndex < 0) return null;
    const next = options
      .slice(currentIndex + 1)
      .find((st) => Number(st?.dueAfterDays) > 0);
    if (!next?.stepId) return null;
    return {
      stepId: next.stepId,
      dueAfterDays: Number(next.dueAfterDays),
      serviceRemainderId: next.serviceRemainderId ?? null,
    };
  } catch {
    return null;
  }
};

const CreateUpdateReminder = ({
  modVsble = false,
  onCloseMod = () => {},
  selectedItem = null,
  selecetdtype = null,
  addToAppointment = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { appointment } = location.state || {};
  const [modValues, setModValues] = useState(initValues);
  const [modErrs, setModErrs] = useState(initErrs);
  // const { locationList, facilities } = useSelector((state) => state?.clinic);
  const apntDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const reduxRemainders = useSelector(
    (state) => state?.clinic?.serviceRemainders,
  );
  const appDet = petAndApntData?.appointment;
  const apntId = petAndApntData?.appointment?.appointmentId;
  const petId = petAndApntData?.pet?.petId;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { services, stocks, clinic, facilities, locations, serviceRemainders } =
    useMemo(() => {
      if (!mandateDataRaw) return defObj;
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinic: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData?.services : [],
          stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData?.facilities
            : [],
          locations: Array.isArray(reqData?.locations)
            ? reqData?.locations
            : [],
          serviceRemainders: Array.isArray(reqData?.serviceRemainders)
            ? reqData.serviceRemainders
            : [],
        };
      } catch {
        return defObj;
      }
    }, [mandateDataRaw]);

  const remainderList = useMemo(() => {
    if (Array.isArray(serviceRemainders) && serviceRemainders.length) {
      return serviceRemainders;
    }
    return Array.isArray(reduxRemainders) ? reduxRemainders : [];
  }, [serviceRemainders, reduxRemainders]);

  useEffect(() => {
    if (!modVsble) return;
    const reqObj = {
      ...initValues,
      type: selecetdtype ?? initValues.type,
      addToAppointment: addToAppointment || initValues.addToAppointment,
      date:
        (selecetdtype === "Diagnostics"
          ? selectedItem?.dueDate
          : selectedItem?.doneAt) ?? new Date(),
      service:
        (selecetdtype === "Diagnostics"
          ? selectedItem?.testName
          : selectedItem?.procedureName) ?? null,
      serviceId: selectedItem?.serviceId ?? null,
      variant:
        (selecetdtype === "Diagnostics"
          ? selectedItem?.variantNumber
          : selectedItem?.serviceType) ?? null,
      variantId:
        (selecetdtype === "Diagnostics"
          ? selectedItem?.variantId
          : selectedItem?.procedureVariantId) ?? null,
      remarks:
        (selecetdtype === "Diagnostics"
          ? selectedItem?.remark
          : selectedItem?.remarks) ?? "",
      planItem: selectedItem?.planItem ?? "",
      category: selectedItem?.category ?? "",
      subCategory: selectedItem?.subCategory ?? "",
      diagType:
        selecetdtype === "Diagnostics" ? (selectedItem?.type ?? "Lab") : null,
      doseInSeries: null,
    };
    if (selecetdtype === "Diagnostics") {
      const matchServ = services?.find(
        (ser) => ser?.serviceId === selectedItem?.serviceId,
      );
      if (matchServ) {
        const reqVar = matchServ?.serviceVariants?.find(
          (vl) => vl?.variantId === selectedItem?.variantId,
        );
        if (reqVar) reqObj.variant = reqVar?.variantName;
      }
    }
    if (selectedItem?.serviceId) {
      reqObj.doseInSeries = findDoseOption(
        getDoseInSeriesList(remainderList, selectedItem?.serviceId),
        selectedItem,
      );
    }
    setModValues(reqObj);
    setModErrs(initErrs);
  }, [modVsble, selecetdtype, addToAppointment, selectedItem]);

  const handleModClose = (typ, catType) => {
    onCloseMod(typ, catType);
  };

  const onChange = (name, value) => {
    const reqObj = { ...modValues, [name]: value };
    const errObj = { ...modErrs };
    if (name === "type") {
      reqObj.service = null;
      reqObj.serviceId = null;
      reqObj.category = null;
      reqObj.subCategory = null;
      reqObj.variant = null;
      reqObj.variantId = null;
      reqObj.doseInSeries = null;

      errObj.service = "";
      errObj.variant = "";
      errObj.planItem = "";
    }
    if (name === "service") {
      reqObj.service = value?.value;
      reqObj.serviceId = value?.serviceId;
      reqObj.category = value?.category;
      reqObj.subCategory = value?.subCategory;
      const vrnts = getVarinatList(value?.serviceId);
      reqObj.variant = vrnts?.[0]?.value;
      reqObj.variantId = vrnts?.[0]?.variantId;
      reqObj.doseInSeries = null;

      errObj.variant = !vrnts?.[0]?.variantId ? "This field is required" : "";
      errObj.service = !value?.serviceId ? "This field is required" : "";
      errObj.planItem = "";
      errObj.doseInSeries = "";
    }
    if (name === "variant") {
      reqObj.variant = value?.value;
      reqObj.variantId = value?.variantId;

      errObj.variant = !value?.variantId ? "This field is required" : "";
    }
    if (name === "planItem") {
      errObj.planItem =
        value?.length > 0 || reqObj?.serviceId ? "" : "This field is required";
    }
    if (name === "doseInSeries") {
      errObj.doseInSeries = value?.stepId ? "" : "This field is required";
    }
    setModValues(reqObj);
    setModErrs(errObj);
  };

  const getServiceList = () => {
    return services
      ?.filter(
        (sl) =>
          sl?.active === "Y" &&
          (modValues?.type === "Preventive"
            ? sl?.category === "Preventive Care"
            : modValues?.type === "Diagnostics"
              ? sl?.category?.toLowerCase() ===
                modValues?.diagType?.toLowerCase()
              : !["lab", "radiology", "preventive care"].includes(
                  sl?.category?.toLowerCase(),
                )),
      )
      ?.map((ser) => ({
        name: ser?.serviceName,
        value: ser?.serviceName,
        serviceId: ser?.serviceId,
        category: ser?.category,
        subCategory: ser?.subCategory,
      }));
  };

  const getVarinatList = (serId) => {
    if (!serId) return [];
    const reqList = services
      ?.find((ser) => ser?.serviceId === serId)
      ?.serviceVariants?.filter(
        (vl) => vl?.createdBy === clinic?.id && vl?.approved,
      )
      ?.map((vnt) => ({
        name: vnt?.variantName,
        variantId: vnt?.variantId,
        servicePrice: vnt?.servicePrice,
        value: vnt?.variantName,
        serviceCharge: vnt?.serviceCharge,
        variantNumber: vnt?.variantNumber,
        serviceId: serId,
      }));
    return reqList ?? [];
  };

  const getNextReminderDueDate = (days) => {
    try {
      const formDate = moment(modValues?.date);
      const visitDate = appDet?.appointmentDate
        ? moment(appDet.appointmentDate)
        : moment();
      const base =
        formDate.isValid() &&
        formDate.format("YYYY-MM-DD") < visitDate.format("YYYY-MM-DD")
          ? visitDate
          : formDate.isValid()
            ? formDate
            : moment();
      return base
        .clone()
        .startOf("day")
        .add(Number(days) || 0, "days")
        .format("YYYY-MM-DD");
    } catch {
      return moment()
        .add(Number(days) || 0, "days")
        .format("YYYY-MM-DD");
    }
  };

  const maybeCreateNextReminder = () => {
    try {
      if (!modValues?.addToAppointment) return Promise.resolve();
      const nextStep = getNextRemainderStep(
        remainderList,
        modValues?.serviceId ?? selectedItem?.serviceId,
        modValues?.doseInSeries,
        selectedItem,
      );
      if (!nextStep?.stepId || !(Number(nextStep.dueAfterDays) > 0)) {
        return Promise.resolve();
      }
      const dueDate = getNextReminderDueDate(nextStep.dueAfterDays);
      if (modValues?.type === "Diagnostics") {
        if (!petId) return Promise.resolve();
        const reqserv = (services ?? []).find(
          (ser) =>
            ser?.serviceId ===
            (modValues?.serviceId ?? selectedItem?.serviceId),
        );
        const reqVar = reqserv?.serviceVariants?.find(
          (sv) =>
            sv?.variantId === (modValues?.variantId ?? selectedItem?.variantId),
        );
        let tax = 0;
        let total = Number(reqVar?.servicePrice ?? 0);
        if (reqserv?.taxApplicability?.toLowerCase() === "yes") {
          tax =
            (Number(reqVar?.servicePrice ?? 0) / 100) *
            Number(reqserv?.tax ?? 0);
          total += tax;
        }
        const reminderForm = new FormData();
        reminderForm.append("type", modValues?.diagType ?? "Lab");
        reminderForm.append("testName", modValues?.service ?? "");
        reminderForm.append("sampleCollected", false);
        reminderForm.append("price", Number(total ?? 0));
        reminderForm.append("tax", Number(tax ?? 0));
        reminderForm.append("dueDate", dueDate);
        reminderForm.append("remark", modValues?.remarks ?? "");
        reminderForm.append("hsnSacCode", Number(reqserv?.hsnSacCode ?? 0));
        reminderForm.append("status", "Scheduled");
        if (modValues?.serviceId) {
          reminderForm.append("serviceId", modValues.serviceId);
        }
        if (modValues?.variantId) {
          reminderForm.append("variantId", modValues.variantId);
        }
        reminderForm.append("doseInSeries", nextStep.stepId);
        reminderForm.append(
          "serviceRemainderId",
          nextStep.serviceRemainderId ?? "",
        );
        return dispatch(
          createDiagnosticsByPetId({
            petId,
            form: reminderForm,
            formData: true,
          }),
        );
      }
      const pet = appDet?.petId ?? petId;
      if (!pet) return Promise.resolve();
      const resServ = (services ?? []).find(
        (ser) =>
          ser?.serviceId === (modValues?.serviceId ?? selectedItem?.serviceId),
      );
      const reqVar = resServ?.serviceVariants?.find(
        (vnt) =>
          vnt?.variantId === modValues?.variantId ||
          vnt?.variantName === modValues?.variant,
      );
      const nextData = {
        procedureName: resServ?.serviceName ?? modValues?.service,
        serviceId: resServ?.serviceId ?? modValues?.serviceId ?? null,
        category: resServ?.category ?? modValues?.category ?? null,
        subCategory: resServ?.subCategory ?? modValues?.subCategory ?? null,
        remarks: modValues?.remarks ?? "",
        doneAt: dueDate,
        status: "Pending",
        petId: pet,
        procedureDetails: [],
        isFromPreventive: modValues?.type === "Preventive",
        planItem: modValues?.planItem ?? "",
        doseInSeries: nextStep.stepId,
        serviceRemainderId: nextStep.serviceRemainderId ?? null,
      };
      if (modValues?.variant || reqVar?.variantName) {
        nextData.serviceType = reqVar?.variantName ?? modValues?.variant;
      }
      if (modValues?.variantId || reqVar?.variantId) {
        nextData.procedureVariantId = reqVar?.variantId ?? modValues?.variantId;
      }
      return dispatch(createProcedure({ data: nextData, successMessage: "" }));
    } catch {
      return Promise.resolve();
    }
  };

  const afterSave = (res) => {
    if (!res?.payload) return;
    Promise.resolve(maybeCreateNextReminder()).finally(() => {
      handleModClose("apiSuccess", modValues?.type);
    });
  };

  const isDoseInSeriesRequired = () =>
    !!modValues?.serviceId &&
    getDoseInSeriesList(remainderList, modValues?.serviceId).length > 0;

  const getFieldNotValid = () => {
    const errObj = { ...modErrs };
    if (modValues?.type !== "Care") {
      if (!modValues?.serviceId || !modValues?.variantId) {
        errObj.service = !modValues?.serviceId ? "This field is required" : "";
        errObj.variant = !modValues?.variantId ? "This field is required" : "";
      }
    }
    if (modValues?.type === "Care") {
      if (modValues?.serviceId && !!modValues?.variantId) {
        errObj.variant = !modValues?.variantId ? "This field is required" : "";
      }
      if (!modValues?.serviceId && modValues?.planItem?.length === 0) {
        errObj.planItem = "This field is required";
      }
    }
    if (
      !selectedItem &&
      isDoseInSeriesRequired() &&
      !modValues?.doseInSeries?.stepId
    ) {
      errObj.doseInSeries = "This field is required";
    }
    setModErrs(errObj);
    return Object.values(errObj)?.some((li) => li?.length > 0);
  };

  const onSubmit = async () => {
    if (getFieldNotValid()) return;
    if (modValues?.type === "Preventive" || modValues?.type === "Care") {
      const resServ = services?.find(
        (ser) => ser?.serviceId === modValues?.serviceId,
      );
      const reqVar =
        resServ?.serviceVariants?.find(
          (vnt) =>
            vnt?.variantId === modValues?.variantId ||
            vnt?.variantName === modValues?.variant,
        ) ?? null;
      const reqQty = 1;
      let tax = 0;
      let total = reqVar?.servicePrice ?? 0;
      if (resServ?.taxApplicability?.toLowerCase() === "yes") {
        tax +=
          (Number(reqVar?.servicePrice ?? 0) / 100) * Number(resServ?.tax ?? 0);
        total += tax;
      }

      const data = {
        procedureName: resServ?.serviceName ?? modValues?.service,
        serviceId: resServ?.serviceId ?? modValues?.serviceId ?? null,
        category: resServ?.category ?? modValues?.category ?? null,
        subCategory: resServ?.subCategory ?? modValues?.subCategory ?? null,
        remarks: modValues?.remarks ?? "",
        doneAt: moment(new Date(modValues?.date)).format("YYYY-MM-DD"),
        status: "Pending",
        petId: appDet?.petId ?? petId,
        procedureDetails: [],
        isFromPreventive: modValues?.type === "Preventive",
        planItem: modValues?.planItem ?? "",
        serviceType: reqVar?.variantName ?? modValues?.variant ?? null,
        procedureVariantId: reqVar?.variantId ?? modValues?.variantId ?? null,
        serviceCharge: reqVar?.servicePrice ?? 0,
        tax: Number(Number(tax) * Number(reqQty)) ?? 0,
        total: Number(Number(total) * Number(reqQty)) ?? 0,
        mrpPrice: Number(reqVar?.servicePrice ?? 0),
        quantity: Number(reqQty),
        hsnSacCode: resServ?.hsnSacCode ?? null,
        paymentId: petAndApntData?.paymentId ?? null,
      };
      if (modValues?.doseInSeries?.stepId) {
        data.doseInSeries = modValues.doseInSeries.stepId;
        data.serviceRemainderId =
          modValues.doseInSeries.serviceRemainderId ?? null;
      }

      if (modValues?.addToAppointment && apntId) {
        const procedureDetailsResult = await getProcedureDetailsNew({
          servId: modValues?.serviceId,
          varntId: modValues?.variantId,
          services: services ?? [],
          clinicStocks: stocks ?? [],
          pharmacyTaxId: clinic?.pharmacyTaxId,
          status: true,
          facilities: facilities ?? [],
          facilityId: appDet?.facilityId,
          patientWorkbook: true,
          locations: locations ?? [],
          clinicId: clinic?.id,
        });
        const procedureDetails = procedureDetailsResult?.procedureDetails;
        if (procedureDetailsResult?.prescriptions?.length > 0) {
          const prescriptionData = {
            status: "Pending",
            remark: "",
            prescription: procedureDetailsResult?.prescriptions,
            issue: "",
            paymentId: petAndApntData?.paymentId ?? null,
          };
          if (apntDet?.prescription?.[0]?.prescriptionId) {
            dispatch(
              updatePrescriptionByIdWoLoader({
                prescriptionId: apntDet?.prescription?.[0]?.prescriptionId,
                data: prescriptionData,
              }),
            );
          } else {
            dispatch(
              createPrescriptionWoLoader({
                appointmentId: appDet?.appointmentId,
                data: prescriptionData,
              }),
            );
          }
        }
        data.appointmentId = appDet?.appointmentId ?? apntId;
        data.procedureDetails = procedureDetails ?? [];
        data.patientWorkbook = true;
        data.mainProcedure = true;
      }
      if (selectedItem) {
        if (modValues?.addToAppointment) {
          dispatch(
            updateProcedureById({ id: selectedItem?.procedureId, data }),
          ).then(afterSave);
        } else {
          dispatch(
            updateProcedureDateAndRemarkById({
              id: selectedItem?.procedureId,
              data: {
                doneAt: moment(new Date(modValues?.date)).format("YYYY-MM-DD"),
                remarks: modValues?.remarks,
                planItem: modValues?.planItem ?? "",
              },
            }),
          ).then(afterSave);
        }
      } else {
        dispatch(createProcedure({ data })).then(afterSave);
      }
    }
    if (modValues?.type === "Diagnostics") {
      const reqserv = services?.find(
        (ser) => ser?.serviceId === modValues?.serviceId,
      );
      const reqVar = reqserv?.serviceVariants?.find(
        (sv) => sv?.variantId === modValues?.variantId,
      );
      let tax = 0;
      let total = reqVar?.servicePrice ?? 0;
      if (reqserv?.taxApplicability?.toLowerCase() === "yes") {
        tax +=
          reqserv?.taxApplicability?.toLowerCase() === "yes"
            ? (Number(reqVar?.servicePrice ?? 0) / 100) *
              Number(reqserv?.tax ?? 0)
            : 0;
        total += tax;
      }
      const form = new FormData();
      if (apntId && modValues?.addToAppointment) {
        form.append("appId", apntId);
      }
      form.append("type", modValues?.diagType);
      form.append("testName", modValues?.service);
      form.append("sampleCollected", false);
      form.append("price", Number(total ?? 0));
      form.append("tax", Number(tax ?? 0));
      form.append(
        "dueDate",
        moment(new Date(modValues?.date)).format("YYYY-MM-DD"),
      );
      form.append("remark", modValues?.remarks);
      form.append("hsnSacCode", Number(reqserv?.hsnSacCode ?? null));
      form.append("status", "Scheduled");
      if (modValues?.serviceId) {
        form.append("serviceId", modValues?.serviceId);
      }
      if (modValues?.variantId) {
        form.append("variantId", modValues?.variantId);
      }
      if (modValues?.doseInSeries?.stepId) {
        form.append("doseInSeries", modValues.doseInSeries.stepId);
        form.append(
          "serviceRemainderId",
          modValues.doseInSeries.serviceRemainderId ?? "",
        );
      }
      if (selectedItem) {
        const metaUpdate = { diagnosticsId: selectedItem?.diagonosticId, form };
        dispatch(updateDiagnostics(metaUpdate)).then(afterSave);
      } else {
        dispatch(
          apntId && modValues?.addToAppointment
            ? createDiagnostics({ appointmentId: apntId, form, formData: true })
            : createDiagnosticsByPetId({ petId, form, formData: true }),
        ).then(afterSave);
      }
    }
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => handleModClose("close", null)}
        header={selectedItem ? "Update" : "Add"}
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-medium fs14">
                Type <span className="red-color">*</span>
              </Typography>
              <CustomSelect
                list={[
                  { name: "Preventive", value: "Preventive" },
                  { name: "Diagnostics", value: "Diagnostics" },
                  { name: "Care", value: "Care" },
                ]}
                value={modValues?.type}
                name="type"
                label=""
                select
                handleChange={(e) => onChange("type", e?.target?.value)}
                disabled={selectedItem}
              />
            </Grid>
            {modValues?.type === "Diagnostics" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="font-medium fs14">
                  Category <span className="red-color">*</span>
                </Typography>
                <CustomSelect
                  list={diagnosticTypes}
                  value={modValues?.diagType}
                  name="diagType"
                  label=""
                  select
                  handleChange={(e) => {
                    onChange("diagType", e?.target?.value);
                  }}
                  disabled={selectedItem}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-medium fs14">
                Item Name{" "}
                {modValues?.type !== "Care" ? (
                  <span className="red-color">*</span>
                ) : null}
              </Typography>
              <CustomSelect
                list={getServiceList()}
                value={modValues?.service?.value ?? modValues?.service}
                name="service"
                label=""
                newSelect
                handleChange={(e) => onChange("service", e)}
                error={!!modErrs?.service}
                helperText={modErrs?.service}
                disabled={selectedItem}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-medium fs14">
                Variant{" "}
                {modValues?.type !== "Care" ? (
                  <span className="red-color">*</span>
                ) : null}
              </Typography>
              <CustomSelect
                list={getVarinatList(modValues?.serviceId)}
                value={modValues?.variant?.value ?? modValues?.variant}
                name="variant"
                label=""
                newSelect
                handleChange={(e) => onChange("variant", e)}
                error={!!modErrs?.variant}
                helperText={modErrs?.variant}
                disabled={selectedItem}
              />
            </Grid>
            {modValues?.type === "Care" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="font-medium fs14">
                  Plan Item
                  {!modValues?.serviceId ? (
                    <span className="red-color">*</span>
                  ) : null}
                </Typography>
                <CustomTextField
                  label=""
                  fullWidth
                  handleChange={(e) => onChange("planItem", e?.target?.value)}
                  value={modValues?.planItem}
                  error={!!modErrs?.planItem}
                  helperText={modErrs?.planItem}
                />
              </Grid>
            ) : null}
            {getDoseInSeriesList(remainderList, modValues?.serviceId).length >
            0 ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="font-medium fs14">
                  {getOccurrenceLabel(remainderList, modValues?.serviceId)} in
                  Series <span className="red-color">*</span>
                </Typography>
                <CustomSelect
                  list={getDoseInSeriesList(
                    remainderList,
                    modValues?.serviceId,
                  )}
                  value={modValues?.doseInSeries?.name}
                  name="doseInSeries"
                  label=""
                  select
                  handleChange={(e) =>
                    onChange(
                      "doseInSeries",
                      getDoseInSeriesList(
                        remainderList,
                        modValues?.serviceId,
                      )?.find((li) => li?.name === e?.target?.value) ?? null,
                    )
                  }
                  disabled={!!selectedItem}
                  error={!!modErrs?.doseInSeries}
                  helperText={modErrs?.doseInSeries}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-medium fs14">
                Date <span className="red-color">*</span>
              </Typography>
              <CustomTextField
                label=""
                fullWidth
                handleChange={(e) => onChange("date", e?.target?.value)}
                value={modValues?.date}
                mobileDate
              />
            </Grid>
            {apntId &&
            !!(
              modValues?.type !== "Care" ||
              (modValues?.type === "Care" &&
                (modValues?.serviceId || selectedItem?.serviceId))
            ) ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mbMin10">
                  <CustomCheckbox
                    label="Mark as Done"
                    checked={modValues?.addToAppointment}
                    onChange={() =>
                      onChange("addToAppointment", !modValues?.addToAppointment)
                    }
                    // disabled={selectedItem}
                  />
                </div>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography className="font-medium fs14">Remarks</Typography>
              <CustomTextField
                label=""
                fullWidth
                handleChange={(e) => onChange("remarks", e?.target?.value)}
                value={modValues?.remarks}
                multiline
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex1-end mt10">
                <div>
                  <CustomButton text="Submit" smallBtn onClick={onSubmit} />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </>
  );
};

export default CreateUpdateReminder;
